import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { TranslocoService } from '@ngneat/transloco'
import {
  Auth,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth'
import { Observable, of, Subscription } from 'rxjs'
import { first, switchMap, tap } from 'rxjs/operators'
import { ApiService } from './api.service'
import { dbActionUrl } from './new-api.util'
import { Cry } from './cry.service'
import { getFirebaseApp } from './firebase-init'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { MenusService } from './menus.service'
import { NotifyService } from './notifications/notify.service'
import { timeOut, use } from './utils'

let env: any
let BUILD: any

@Injectable({ providedIn: "root" })
export class AuthService {
  user: Observable<any | null>
  afAuth: Auth | null = null
  private fss: FirestoreService2

  constructor(
    private router: Router,
    private notify: NotifyService,
    public g: GlobalService,
    private http: HttpClient,
    private menuService: MenusService,
    private translocoSrv: TranslocoService,
  ) {
    env = (window as any).y4wenvs.ENV
    BUILD = (window as any).y4wenvs.BUILD

    // test for SSR
    if (this.g.isBrowser) {
      const app = getFirebaseApp(env.firebaseConfig)
      this.afAuth = getAuth(app)
      this.init()
    }
  }

  private async init() {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const isAppTokenRoute = this.g.isBrowser && window.location.pathname.indexOf('app-token') > -1
    if (this.g.program && this.g.program.logins.anonymous && !isAppTokenRoute && !this.g.isBoDomain && !this.g.isAdminDomain) {
      await this.anonymousLogin().then(() => this.router.navigate([`/${this.g.program.type}`]))
    }

    this.user = this.onAuthStateChanged().pipe(
      switchMap((authUser: any) => (authUser ? this.fss.get('users', authUser.uid, { api: false }) : of(null))),
      tap(user => { if (user) { this.g.set('user', user); this.menuService.setProfile(user); this.menuService.loadMenus() } }),
      tap(user => { if (user) this.listenBatchLogoff() }),
    )
  }

  private onAuthStateChanged() {
    return new Observable<any>(obs => {
      const unsub = onAuthStateChanged(this.afAuth, user => obs.next(user), err => obs.error(err))
      return () => unsub()
    })
  }

  anonymousLogin() {
    const api = use<ApiService>(ApiService)
    return FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => {
        // This is the unique browser identifier
        const visitorId = result.visitorId
        return api.post('users/userAction', { action: 'anonymousLogin', uid: visitorId, email: `${visitorId}@yesanon.com` }, true).pipe(first()).toPromise()
          .then((anon: any) => {
            const _anon = anon
            return signInWithCustomToken(this.afAuth, _anon.token)
              .then(async (result) => {
                let dbUser = await this.fss.get('users', result.user.uid, { api: true }).pipe(first()).toPromise()
                if (!dbUser) {
                  dbUser = {
                    active: true,
                    id: result.user.uid,
                    uid: result.user.uid,
                    displayName: 'Convidado',
                    firstName: 'Convidado',
                    nick: this.g.nick,
                    role: 'user',
                    email: result.user.email || `${visitorId}@yesanon.com`,
                    isAnonymous: true,
                    createdAt: new Date().toISOString(),
                  }
                  // creates the user via cripted API
                  return this.http.post(dbActionUrl(this.g.apiUrl, this.g.newApiUrl), Cry.crypt(JSON.stringify({
                    action: 'createUser',
                    id: result.user.uid,
                    nick: this.g.nick,
                    data: dbUser
                  }), true)).pipe(first()).toPromise().then(() => {
                    api.post('users/updateUserAuth', { uid: result.user.uid, displayName: dbUser.displayName, email: dbUser.email }, true)
                      .pipe(first()).toPromise().then(ok => !env.production && console.log('#updateUserAuth OK', ok)).catch(_er => console.log('#updateUserAuth', _er.message))
                    this.g.set('user', dbUser)
                    return result.user
                  })
                } else {
                  this.g.set('user', dbUser)
                  return result.user
                }
              })
          })
      })
  }

  emailSignUp(email: string, password: string) {
    return createUserWithEmailAndPassword(this.afAuth, email, password)
      .then(credential => this.updateUserData(credential.user))
    // .catch(error => this.handleError(error))
  }

  emailLogin(email: string, password: string) {
    return signInWithEmailAndPassword(this.afAuth, email, password)
      .then(credential => credential.user)
  }

  signInWithCustomToken(token: string) {
    return signInWithCustomToken(this.afAuth, token)
      .then(credential => credential.user)
  }

  resetPassword(email: string) {
    sendPasswordResetEmail(this.afAuth, email)
      .then(() => this.notify.update('Password update email sent', 'btn-success', 3000))
      .catch(error => this.handleError(error))
  }

  resetPasswordEmail(body = null): Promise<any> {
    if (body === null) {
      const _program = {
        name: this.g.program.name,
        nick: this.g.nick,
        email: this.g.program.email,
        sender: this.g.program.sender,
        imgHeader: this.g.program.images['emailHeader'],
        imgFooter: this.g.program.images['emailFooter'],
        templateId: this.g.program.emails['resetPassword'],
        url: this.g.program.url
      }
      body = {
        nick: this.g.nick,
        email: this.g.user.email,
        displayName: this.g.user.displayName,
        program: _program
      }
    }
    // The API builds the link and injects the single-use reset nonce, so the
    // client no longer mints the token. `users/userAction` is routed to the new
    // API by ApiService.
    const api: ApiService = use<ApiService>(ApiService)
    return api.post('users/userAction', { ...body, action: 'resetPassword' }, true).pipe(first())
      .toPromise()
      .then((_ok: any) => {
        if (_ok?.success) this.notify.update(this.translocoSrv.translate('email_sent_check_your_mailbox'), 'btn-success', 8000)
        else if (_ok?.error) this.notify.update(_ok.error, 'btn-danger', 5000)
      })
  }

  signOut(navigate = true) {
    signOut(this.afAuth).then(() => {
      Cry.removeLocal('bo-nick')
      if (navigate) this.router.navigate(['/login'])
      this.g.set('user', null, true)
    })
  }

  // If error, console log and notify user
  private handleError(error: Error) {
    this.notify.update(error.message, 'btn-danger', 3000)
  }

  // Sets user data to firestore after succesful signup
  updateUserData(user, setGlobal = true) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const data = {
      uid: user.uid,
      isAnonymous: user.isAnonymous || false,
      email: user.email || null,
      displayName: user.displayName || 'nameless user',
      photoURL: user.photoURL || 'https://goo.gl/Fz9nrQ',
      role: 'user'
    }

    if (setGlobal) this.g.set('user', data) // added by walter
    return this.fss.merge('users', user.uid, data).then(() => data)
  }

  // Used by the http interceptor to set the auth header
  getUserIdToken(): Observable<string> {
    if (this.afAuth && this.afAuth.currentUser) {
      return new Observable<any>(obs => {
        this.afAuth.currentUser.getIdToken().then(token => { obs.next(token); obs.complete() }).catch(e => obs.error(e))
      })
    }

    return of(null)
  }

  isSuperUser = (user: any) => {
    return [
      'mrsilva@yesmkt.com',
      'waltergandarella@yesmkt.com',
      'pedroferreiro@yesmkt.com'
    ].indexOf(user.email) > -1
  }

  /**
   * Centralized redirect logic after user authentication.
   * Priority chain:
   *  1. Domain rules (isBoDomain / isAdminDomain) → /admin
   *  2. Stored redirect (deep-link before login) → stored URL
   *  3. segmentsRedirectByType (per-program exception) → redirectBySegmentType
   *  4. mapRolesToHome (per-program role mapping) → mapped path
   *  5. Default fallback → [type]/home
   */
  async redirectUser(user?: any): Promise<void> {
    try {
      const u = user || this.g.user
      if (this.g.isBoDomain || this.g.isAdminDomain) {
        this.router.navigate(['/admin'])
      } else if (this.g.get('redirect') && this.g.get('redirect') !== '/') {
        const storedRedirect = this.g.get('redirect')
        this.g.set('redirect', null)
        Cry.remove('redirect')
        // navigateByUrl parses the full URL (path + query); navigate([string]) would encode the query into the path
        this.router.navigateByUrl(storedRedirect)
      } else if (this.g.program?.options?.segmentsRedirectByType) {
        this.redirectBySegmentType(u, true)
      } else {
        console.log('# mapRolesToHome')
        const map = this.g.program?.mapRolesToHome
        const mappedPath = Array.isArray(map)
          ? map.find(m => m.role === u?.role)?.path
          : map?.[u?.role]
        console.log(mappedPath)
        if (mappedPath?.startsWith('/')) {
          this.router.navigate([mappedPath])
        } else {
          this.router.navigate([this.g.type, 'home'])
        }
      }
    } catch (e: any) {
      this.saveDebug({ message: `# redirectUser ERROR `, stack: JSON.stringify(e) })
      this.router.navigate([this.g.type, 'home'])
    }
  }

  afterSignIn(authUser: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return new Promise((resolve, reject) => {
      if (!authUser || (authUser && authUser.error)) {
        this.notify.update(authUser.error, 'btn-danger', 3000)
        reject(authUser.error || 'noauth')
      } else {
        this.fss.get('users', authUser['uid'], { api: true }).toPromise().then(async user => {
          if (user && user.active) {
            this.saveLastLogin()
            this.g.set('user', user)
            this.fss.merge('users', user.uid, { lastLogin: new Date().toISOString(), firstLogin: user.firstLogin || new Date().toISOString() }, { silent: true })
            await this.redirectUser(user)
            resolve(user)
          } else {
            timeOut(() => this.signOut(false), 500)
            reject(!user ? 'auth-user-not-found' : 'auth-user-not-active')
          }
        })
      }
    })
  }

  async redirectBySegmentType(user: any, login: boolean = false) {
    try {
      const path = window.location.pathname
      const api: ApiService = use<ApiService>(ApiService)
      const fss: FirestoreService2 = use<FirestoreService2>(FirestoreService2)
      const segmentsMapping = await fss.toPromise(api.post('users/userAction?noAuthRequest=true', {
        action: 'listSegmentsMapping',
        nick: this.g.nick,
      }, true))
      if (user && user.uid && user.active) {
        const userSegments = user.segments || []
        if (!userSegments.filter(o => o === user.segment?.id).length) userSegments.push(user.segment?.id)

        let matched = false
        for (const map of (segmentsMapping.result || [])) {
          if (userSegments.includes(map.segment)) {
            matched = true
            Cry.set('type', map.type)
            this.g.set('type', map.type)
            if (login || path === '/') this.router.navigate([`/${map.type}/home`])
          }
        }
        if (!matched && (login || path === '/')) this.router.navigate([this.g.type, 'home'])
      }
    } catch (e) {
      this.router.navigate([this.g.type, 'home'])
    }
  }

  private batchLogoffSub: Subscription

  private listenBatchLogoff() {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    if (this.batchLogoffSub) this.batchLogoffSub.unsubscribe()
    this.batchLogoffSub = this.fss.list('logs', { where: [['type', '==', 'batchLogoff']], limit: 1, order: 'createdAt', direction: 'desc' })
      .subscribe(logs => {
        if (logs.length) {
          const log = logs[0]
          const ls = localStorage.getItem(`${log.type}-${log.id}`)
          const time = new Date(log.createdAt).getTime()
          const now = new Date().getTime()
          if (ls === null && log.active && (now - time < (86400000 * 30))) {
            const currentUser = this.g.get('user')
            if (!currentUser) return
            this.fss.get('users', currentUser.id, { api: true }).subscribe(user => {
              if (!user.isAnonymous) {
                timeOut(() => {
                  console.log('# batch logoff')
                  localStorage.setItem(`${log.type}-${log.id}`, 'true')
                  this.signOut(true)
                  if (window) window.location.reload()
                }, 4000)
              }
            })
          }
        }
      })
  }

  ////// temporarily

  saveDebug(error: any) {
    const user = this.g.get('user')
    const err = {
      error: {
        message: error.message,
        stack: error.stack || '',
        name: error.name || ''
      },
      path: window.location.href,
      user: user ? user.email : 'indefinido',
      nick: this.g.nick,
      timestamp: new Date().getTime(),
      createdAt: new Date().toISOString(),
      build: BUILD
    }

    const api = use<ApiService>(ApiService)
    api.post('users/userAction?noAuthRequest', { action: 'debug', err }, true).pipe(first()).toPromise()
  }

  saveLastLogin() {
    Cry.setLocal('lastLogin', Date.now(), true)
  }

}
